import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SupportTicket {
  id: string;
  name: string;
  email: string;
  message: string;
  createdAt: string;
}

interface ReportItem {
  id: string;
  type: 'product' | 'seller';
  targetId?: string;
  name: string;
  email: string;
  reason: string;
  createdAt: string;
}

async function fetchServerList<T>(path: string): Promise<T[] | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) throw new Error('bad response');
    return (await res.json()) as T[];
  } catch {
    return null;
  }
}

function loadLocal<T>(key: string): T[] {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
}

const AdminSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [tab, setTab] = useState<'support' | 'reports'>('support');
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Check if admin is authenticated via session storage
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/admin/auth/sign-in');
      return;
    }

    (async () => {
      const serverTickets = await fetchServerList<SupportTicket>('/api/admin/support');
      const serverReports = await fetchServerList<ReportItem>('/api/admin/reports');
      setTickets(serverTickets ?? loadLocal<SupportTicket>('supportTickets'));
      setReports(serverReports ?? loadLocal<ReportItem>('reports'));
    })();
  }, [navigate]);

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(t => [t.name, t.email, t.message].some(v => (v || '').toLowerCase().includes(q)));
  }, [tickets, query]);

  const filteredReports = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter(r => [r.name, r.email, r.reason, r.type, r.targetId || ''].some(v => (v || '').toLowerCase().includes(q)));
  }, [reports, query]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">Support & Reports</h1>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setTab('support')} className={`px-3 py-1.5 rounded-md text-sm md:text-base ${tab==='support'?'bg-blue-600 text-white':'bg-gray-100 text-gray-800'}`}>Support</button>
        <button onClick={() => setTab('reports')} className={`px-3 py-1.5 rounded-md text-sm md:text-base ${tab==='reports'?'bg-blue-600 text-white':'bg-gray-100 text-gray-800'}`}>Reports</button>
        <div className="flex-1"></div>
        <input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Search by name, email, id…" className="w-52 md:w-72 rounded-md border border-gray-300 px-3 py-1.5 text-sm md:text-base" />
      </div>

      {tab === 'support' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {filteredTickets.map(t => (
            <div key={t.id} className="bg-white rounded-lg shadow-sm p-4 md:p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900 text-sm md:text-base">{t.name}</div>
                <div className="text-xs md:text-sm text-gray-600">{new Date(t.createdAt).toLocaleString()}</div>
              </div>
              <div className="text-xs md:text-sm text-blue-700 mb-2">{t.email}</div>
              <div className="text-sm md:text-base text-gray-800 whitespace-pre-wrap">{t.message}</div>
            </div>
          ))}
          {filteredTickets.length === 0 && (
            <div className="text-center text-gray-600">No support messages yet.</div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {filteredReports.map(r => (
            <div key={r.id} className="bg-white rounded-lg shadow-sm p-4 md:p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900 text-sm md:text-base">{r.name}</div>
                <div className="text-xs md:text-sm text-gray-600">{new Date(r.createdAt).toLocaleString()}</div>
              </div>
              <div className="text-xs md:text-sm text-gray-600 mb-1">Type: <span className="font-medium text-gray-900">{r.type}</span></div>
              {r.targetId && (
                <div className="text-xs md:text-sm text-gray-600 mb-1">Target ID: <span className="font-medium text-gray-900">{r.targetId}</span></div>
              )}
              <div className="text-xs md:text-sm text-blue-700 mb-2">{r.email}</div>
              <div className="text-sm md:text-base text-gray-800 whitespace-pre-wrap">{r.reason}</div>
            </div>
          ))}
          {filteredReports.length === 0 && (
            <div className="text-center text-gray-600">No reports yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSupportPage;